import React from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import theme from '../theme';

export type SubscriptionAlertVariant = 'active' | 'cancelled' | 'paused';

interface SubscriptionAlertProps {
  variant: SubscriptionAlertVariant;
  title: string;
  description: string;
  nextBillingDate?: string;
  actionLabel?: string;
  onAction?: () => void;
  onClose?: () => void;
}

const variantStyles = {
  active: {
    background: '#1976d2',
    color: '#fff',
    subtitle: "#E8E8EA",
    border: `1px solid ${theme.palette.primary.main}`,
    button: {
      color: theme.palette.primary.dark,
      background: '#fff',
      border: `1px solid ${theme.palette.primary.dark}`,
    },
  },
  cancelled: {
    background: '#F9DADE',
    color: '#E44258',
    subtitle: "#333333",
    border: '1px solid #E44258',
    button: {
      color: '#E44258',
      background: '#fff',
      border: '1px solid #E44258',
    },
  },
  paused: {
    background: '#F9EEDA',
    color: '#B38A0A',
    subtitle: "#333333",
    border: '1px solid #DAA603',
    button: {
      color: '#B38A0A',
      background: '#fff',
      border: '1px solid #B38A0A',
    },
  },
};

export const SubscriptionAlert: React.FC<SubscriptionAlertProps> = ({
  variant,
  title,
  description,
  nextBillingDate,
  actionLabel,
  onAction,
  onClose,
}) => {
  const styles = variantStyles[variant];
  return (
    <Box
      sx={{
        background: styles.background,
        color: styles.color,
        border: styles.border,
        borderRadius: 2,
        p: '12px 32px',
        mb: 2,
        gap: '12px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        boxShadow: 0,
        position: 'relative',
      }}
    >
      <Box>
        <Typography fontWeight={500} fontSize="min(18px, calc(16px + 2 * ((100vw - 320px) / (1920 - 320))))" mb="12px" color={styles.color}>
          {title}
        </Typography>
        {nextBillingDate && (
          <Typography variant='subtitle1' fontSize="min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))" component="span" color={styles.subtitle}>
            Next Billing date is <b style={{ color: styles.color }}>{nextBillingDate}</b>.&nbsp;
          </Typography>
        )}
        <Typography variant='subtitle1' fontSize="min(16px, calc(14px + 2 * ((100vw - 320px) / (1920 - 320))))" component="span" color={styles.subtitle}>
          {description}
        </Typography>
      </Box>
      <Box 
        display="flex" 
        alignItems="center" 
        gap={2}
        justifyContent={{ xs: 'space-between', md: '' }}
        width={{ xs: '100%', md: 'auto' }}
      >
        {actionLabel && (
          <Button
            variant="outlined"
            size="large"
            onClick={onAction}
            sx={{
              ...styles.button,
              fontWeight: 500,
              textTransform: 'none',
              '&:hover': {
                background: styles.button.background,
                opacity: 0.9,
              },
            }}
          >
            {actionLabel}
          </Button>
        )}
        {onClose && (
          <IconButton onClick={onClose} sx={{ color: styles.color }} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default SubscriptionAlert; 